import { observer } from 'mobx-react';
import { useContext, useState } from 'react';
import SectionTypeSelector from '../SectionTypeSelector';
import CuratedSectionsModal from '../CuratedSectionsModal';
import SectionsEmptyState from '../SectionsEmptyState';
import { useSectionTypeOptions } from '../../hooks/useSectionTypeOptions';
import { ExperienceResourceArray } from '../../Types';
import { T, TranslationsContext, translation } from '../../../../../../../Translations';

interface Props {
  onSelectBlank: (insertionIndex?: number) => void;
  onSelectBrowseAll: (insertionIndex?: number) => void;
  onSelectCurated: (curatedSectionIds: number[], insertionIndex?: number) => void;
  canAddBrowseAll: boolean;
  experiencesArray: ExperienceResourceArray;
  isEmpty?: boolean;
  insertionIndex?: number;
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

interface InsertionZoneProps {
  onInsert: () => void;
  intercomTarget: string;
}

function InsertionZone({ onInsert, intercomTarget }: InsertionZoneProps) {
  const [isZoneHovered, setIsZoneHovered] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const ts = useContext(TranslationsContext);

  return (
    <div
      className="relative py-8"
      onMouseEnter={() => setIsZoneHovered(true)}
      onMouseLeave={() => setIsZoneHovered(false)}
    >
      <div
        className={`h-px border-t transition-all duration-200 ${
          isZoneHovered ? 'border-dashed border-primary/50' : 'border-transparent'
        }`}
      />

      <div
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-opacity duration-200 ${
          isZoneHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-background -z-10" style={{ margin: '-4px' }} />

        <button
          type="button"
          onClick={onInsert}
          aria-label={translation('Add a section')(ts)}
          className="px-4 py-2 bg-card text-primary rounded transition-all inline-flex items-center gap-2 cursor-pointer"
          style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-weight-medium)',
            backgroundColor: isButtonHovered ? 'rgba(24, 122, 144, 0.1)' : undefined,
            boxShadow: 'inset 0 0 0 1px var(--border)',
          }}
          onMouseEnter={() => setIsButtonHovered(true)}
          onMouseLeave={() => setIsButtonHovered(false)}
          data-intercom-target={intercomTarget}
        >
          <PlusIcon className="w-4 h-4" />
          <T kind="Add a section" />
        </button>
      </div>
    </div>
  );
}

interface SectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

function SectionModal({ isOpen, onClose, children }: SectionModalProps) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
      style={{ zIndex: 1000 }}
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}

function AddSections({
  onSelectBlank,
  onSelectBrowseAll,
  onSelectCurated,
  canAddBrowseAll,
  experiencesArray,
  isEmpty = false,
  insertionIndex,
}: Props) {
  const [showCuratedModal, setShowCuratedModal] = useState(false);
  const [showSectionTypeModal, setShowSectionTypeModal] = useState(false);
  const { sectionTypeOptionsState } = useSectionTypeOptions();

  const curatedSections =
    sectionTypeOptionsState.status === 'success'
      ? sectionTypeOptionsState.data?.payload.curated ?? []
      : [];
  const isLoadingCurated = sectionTypeOptionsState.status === 'pending';

  const handleCuratedModalClose = () => {
    setShowCuratedModal(false);
  };

  const handleCuratedModalApply = (selectedIds: number[]) => {
    setShowCuratedModal(false);
    onSelectCurated(selectedIds, insertionIndex);
  };

  if (isEmpty) {
    return (
      <>
        <SectionsEmptyState
          onSelectBlank={() => onSelectBlank(insertionIndex)}
          onSelectBrowseAll={() => onSelectBrowseAll(insertionIndex)}
          onSelectCurated={() => setShowCuratedModal(true)}
          canAddBrowseAll={canAddBrowseAll}
          curatedSections={curatedSections}
          isLoadingCurated={isLoadingCurated}
        />
        <CuratedSectionsModal
          isOpen={showCuratedModal}
          onClose={handleCuratedModalClose}
          onApply={handleCuratedModalApply}
          curatedSections={curatedSections}
          isLoading={isLoadingCurated}
          experiencesArray={experiencesArray}
        />
      </>
    );
  }

  return (
    <>
      <InsertionZone
        onInsert={() => setShowSectionTypeModal(true)}
        intercomTarget="builder-add-section-button"
      />

      <SectionModal isOpen={showSectionTypeModal} onClose={() => setShowSectionTypeModal(false)}>
        <SectionTypeSelector
          onSelectBlank={() => {
            setShowSectionTypeModal(false);
            onSelectBlank(insertionIndex);
          }}
          onSelectBrowseAll={() => {
            setShowSectionTypeModal(false);
            onSelectBrowseAll(insertionIndex);
          }}
          onSelectCurated={() => {
            setShowSectionTypeModal(false);
            setShowCuratedModal(true);
          }}
          onClose={() => setShowSectionTypeModal(false)}
          canAddBrowseAll={canAddBrowseAll}
          curatedSections={curatedSections}
          isLoadingCurated={isLoadingCurated}
        />
      </SectionModal>

      <CuratedSectionsModal
        isOpen={showCuratedModal}
        onClose={handleCuratedModalClose}
        onApply={handleCuratedModalApply}
        curatedSections={curatedSections}
        isLoading={isLoadingCurated}
        experiencesArray={experiencesArray}
      />
    </>
  );
}

export default observer(AddSections);
